/**
 * Welcome Step Component
 *
 * First step of the wizard - introduces the app and its features.
 */

import { useTranslation } from '@/hooks/useTranslation';
import { publicAsset } from '@/utils/assetUrl';

interface WelcomeStepProps {
  onNext: () => void;
  onSkip: () => void;
}

const FEATURES = [
  {
    icon: '🎨',
    titleKey: 'wizard.welcome.feature.image.title',
    titleDefault: 'AI Image Generation',
    descKey: 'wizard.welcome.feature.image.desc',
    descDefault: 'Create stunning images with Stable Diffusion XL',
  },
  {
    icon: '🎤',
    titleKey: 'wizard.welcome.feature.tts.title',
    titleDefault: 'Text-to-Speech',
    descKey: 'wizard.welcome.feature.tts.desc',
    descDefault: 'Generate natural voiceovers in multiple languages',
  },
  {
    icon: '🌍',
    titleKey: 'wizard.welcome.feature.translate.title',
    titleDefault: 'Translation',
    descKey: 'wizard.welcome.feature.translate.desc',
    descDefault: 'Translate between 200+ languages locally',
  },
  {
    icon: '🎬',
    titleKey: 'wizard.welcome.feature.video.title',
    titleDefault: 'Video Creation',
    descKey: 'wizard.welcome.feature.video.desc',
    descDefault: 'Automated video editing with AI-powered tools',
  },
];

export function WelcomeStep({ onNext, onSkip }: WelcomeStepProps) {
  const { t } = useTranslation();

  return (
    <div className="text-center space-y-8">
      {/* Logo/Icon */}
      <div className="flex justify-center">
        <img
          src={publicAsset('logo-192.png')}
          alt="Asgard Studio"
          className="w-24 h-24 rounded-2xl shadow-lg"
        />
      </div>

      {/* Title */}
      <div>
        <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 dark:text-secondary-100 mb-3">
          {t('wizard.welcome.title', 'Welcome to Asgard Studio!')}
        </h2>
        <p className="text-lg text-secondary-600 dark:text-secondary-400 max-w-xl mx-auto">
          {t('wizard.welcome.subtitle', "Let's set up your AI-powered video creation environment")}
        </p>
      </div>

      {/* Features grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
        {FEATURES.map((feature) => (
          <div
            key={feature.titleKey}
            className="p-4 rounded-xl bg-secondary-50 dark:bg-secondary-800/50 border border-secondary-200 dark:border-secondary-700"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{feature.icon}</span>
              <div>
                <h3 className="font-semibold text-secondary-900 dark:text-secondary-100">
                  {t(feature.titleKey, feature.titleDefault)}
                </h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                  {t(feature.descKey, feature.descDefault)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 max-w-xl mx-auto">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          {t(
            'wizard.welcome.info',
            "We'll check your system and help you set up the AI components. This takes about 2 minutes."
          )}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          onClick={onNext}
          className="w-full sm:w-auto px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors min-h-[44px]"
        >
          {t('wizard.welcome.getStarted', 'Get Started')}
        </button>
        <button onClick={onSkip} className="icon-button-muted">
          {t('wizard.welcome.skipSetup', 'Skip setup for now')}
        </button>
      </div>
    </div>
  );
}

export default WelcomeStep;
